import {useMutation} from "@apollo/client";
import pluralize from "pluralize";
import {useCallback, useMemo, useRef, useState} from "react";
import {useFormContext} from "react-hook-form";
import {toast} from "react-hot-toast";
import {useNavigate} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {CheckLimitField} from "@/lib/queries/billing.ts";
import {BulkCreateChecksInput, BulkCreateChecksResponse, GQL_BULK_CREATE_CHECKS} from "@/lib/queries/checks.ts";
import {AccountSubscriptionType, AccountType} from "@/lib/types/accounts.ts";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {QueryError, ResponseStatus} from "@/lib/types/common.ts";
import {
  AnalyzerCreatedSnippet,
  AnalyzerCreateFormValues,
  AnalyzerPhase,
  CheckLimitStatus,
} from "@/pages/analyzer/Analyzer.types.ts";
import {
  buildCheckLimitTierLookup,
  checksUrlForTags,
  flattenServerErrors,
  getCheckLimitStatuses,
  getUncreatedMemberIds,
  isRumCheck,
  proposalToBulkCheckInput,
  resolveContactGroupIds,
  resolveTagInputs,
  splitChecksBySection,
} from "@/pages/analyzer/Analyzer.utils.ts";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";
import {LimitCheckerComponent} from "@/pages/billing/v2/InstantCheckout/LimitCheckWithInstantCheckout.tsx";

export const useBulkCreateChecks = () => {
  const {user} = useCurrentUserContext();
  const navigate = useNavigate();
  const {
    phase,
    formData,
    proposedChecks,
    selectedChecks,
    accountUsage,
    refetchUsage,
    refetchStats,
    creationOutcome,
    applyCreationOutcome,
    setIsCreating,
    getThreadId,
    getPreCreatedTagsCount,
    resetPreCreatedTagsCount,
  } = useAnalyzerContext();
  const isBilling2 = user?.account.subscription_type === AccountSubscriptionType.BILLING_2_0;
  const canPurchase =
    !!user && (user.is_primary || (user.account.type === AccountType.REGULAR && user.has_purchase_permission));

  const [bulkCreateChecks, {loading: isMutating}] = useMutation<BulkCreateChecksResponse>(GQL_BULK_CREATE_CHECKS);

  const methods = useFormContext<AnalyzerCreateFormValues>();

  const createdByProposalId = useMemo(() => creationOutcome?.createdByProposalId ?? {}, [creationOutcome]);
  const selectedProposals = useMemo(
    () =>
      proposedChecks.filter(
        (check) =>
          selectedChecks[check.id] &&
          (!createdByProposalId[check.id] || getUncreatedMemberIds(check, createdByProposalId).length > 0)
      ),
    [proposedChecks, selectedChecks, createdByProposalId]
  );
  const newProposals = useMemo(
    () => selectedProposals.filter(({id}) => !createdByProposalId[id]),
    [selectedProposals, createdByProposalId]
  );
  const newSelection = useMemo(
    () => Object.fromEntries(Object.entries(selectedChecks).filter(([id]) => !createdByProposalId[id])),
    [selectedChecks, createdByProposalId]
  );

  const newCount = newProposals.length;
  const isGrowOnly = newCount === 0;
  const createLabel = isGrowOnly
    ? `Update ${selectedProposals.length} ${pluralize("Check", selectedProposals.length)}`
    : `Create ${newCount} ${pluralize("Check", newCount)}`;

  const selectedRumCount = useMemo(() => selectedProposals.filter(isRumCheck).length, [selectedProposals]);
  const createdRumCount = useMemo(
    () => proposedChecks.filter((check) => isRumCheck(check) && createdByProposalId[check.id]).length,
    [proposedChecks, createdByProposalId]
  );

  const limitCheckRef = useRef<LimitCheckerComponent>();
  const [overLimitStatuses, setOverLimitStatuses] = useState<CheckLimitStatus[]>([]);
  const [snippets, setSnippets] = useState<AnalyzerCreatedSnippet[]>([]);

  const runCreate = useCallback(
    async (values: AnalyzerCreateFormValues) => {
      if (!formData) return;
      const proposals = selectedProposals;
      const threadId = getThreadId();
      const input: BulkCreateChecksInput = {
        ...(threadId && {thread_id: threadId, pre_created_tags_count: getPreCreatedTagsCount()}),
        checks: proposals.map((check) => ({
          ...proposalToBulkCheckInput(formData, check, createdByProposalId),
          ...(check.tags && {tags: resolveTagInputs(check.tags, values.tags, formData.tags)}),
          ...(check.contact_groups && {
            contact_groups: resolveContactGroupIds(check.contact_groups, formData.contactGroups.items),
          }),
        })),
        tags: values.tags.map((tag) => (tag.id ? {id: tag.id} : {tag: tag.tag.trim(), color_hex: tag.color_hex})),
        contact_groups: values.contactGroups,
      };
      setIsCreating(true);
      const result = await bulkCreateChecks({variables: {input}})
        .catch(() => undefined)
        .finally(() => setIsCreating(false));
      const response = result?.data?.response;
      if (!response) {
        toast.error("Something went wrong. Please try again later or contact support for assistance.");
        return;
      }
      if (response.status !== ResponseStatus.SUCCESS) {
        const message = (response as QueryError).message;
        const tagErrors = message.tags;
        if (tagErrors) {
          methods.setError("tags", {message: tagErrors.join(" ")});
        } else if (!message.__all__) {
          toast.error(flattenServerErrors(message));
        }
        return;
      }
      resetPreCreatedTagsCount();
      const {created, failed, tags} = response.data;
      const nextCreatedByProposalId = {...createdByProposalId};
      const uuidByProposalId = {...(creationOutcome?.uuidByProposalId ?? {})};
      const errorsByProposalId = {...(creationOutcome?.errorsByProposalId ?? {})};
      created.forEach((check) => {
        if (check.proposal_id) {
          nextCreatedByProposalId[check.proposal_id] = check.id;
          uuidByProposalId[check.proposal_id] = check.uuid;
          delete errorsByProposalId[check.proposal_id];
        }
      });
      failed.forEach((failure) => {
        if (failure.proposal_id) {
          errorsByProposalId[failure.proposal_id] = flattenServerErrors(failure.errors);
        }
      });
      const failedCount = Object.keys(errorsByProposalId).length;
      methods.setValue(
        "tags",
        tags.map((tag) => ({id: tag.id, tag: tag.tag, color_hex: tag.color_hex}))
      );
      const tagIds = tags.map((tag) => tag.id);
      applyCreationOutcome({
        createdByProposalId: nextCreatedByProposalId,
        uuidByProposalId,
        errorsByProposalId,
        tagIds,
      });
      refetchUsage();
      if (isBilling2) {
        refetchStats();
      }
      const proposalsById = new Map(proposals.map((check) => [check.id, check]));
      const createdSnippets = created.flatMap(({proposal_id, uuid}) => {
        const proposal = proposal_id ? proposalsById.get(proposal_id) : undefined;
        if (!proposal || !isRumCheck(proposal)) {
          return [];
        }
        return [{name: proposal.name, host: proposal.msp_address, uuid}];
      });
      if (!failedCount) {
        const newCount = created.filter(({proposal_id}) => !proposal_id || !createdByProposalId[proposal_id]).length;
        const tagged = tags.length ? " and tagged" : "";
        const summary = newCount
          ? `${newCount} ${pluralize("Check", newCount)} created${tagged}`
          : `${pluralize("Group", created.length)} updated`;
        const snippetHint = createdSnippets.length
          ? " Install the RUM snippets on your site to start collecting data."
          : "";
        toast.success(`${summary}.${snippetHint}`);
      }
      if (createdSnippets.length) {
        setSnippets(createdSnippets);
      } else if (!failedCount) {
        navigate(checksUrlForTags(tagIds));
      }
    },
    [
      formData,
      selectedProposals,
      getThreadId,
      getPreCreatedTagsCount,
      resetPreCreatedTagsCount,
      bulkCreateChecks,
      creationOutcome,
      createdByProposalId,
      methods,
      applyCreationOutcome,
      setIsCreating,
      refetchUsage,
      refetchStats,
      isBilling2,
      navigate,
    ]
  );

  const isOverBilling2Limit = async (): Promise<boolean> => {
    const stats = await refetchStats();
    if (!stats || !accountUsage) {
      return false;
    }
    const {basic, advanced, group} = splitChecksBySection(newProposals, accountUsage);
    const limits = [
      {checks: basic, pair: stats.basic_checks, limitField: CheckLimitField.BASIC_CHECKS},
      {checks: advanced, pair: stats.advanced_checks, limitField: CheckLimitField.ADVANCED_CHECKS},
      {checks: group, pair: stats.group_checks, limitField: CheckLimitField.GROUP_CHECKS},
    ]
      .filter((limit) => limit.checks.length)
      .map((limit) => ({...limit, requested: limit.pair[0] + limit.checks.length}));
    const primary = limits.find((limit) => limit.requested > limit.pair[1]) ?? limits[0];
    if (!primary) {
      return false;
    }
    const isLimitHit = await limitCheckRef.current?.checkIfLimitHit({
      serviceType: primary.checks[0].monitoring_service_type as MonitoringServiceType,
      requestedValue: primary.requested,
      additionalLimits: limits
        .filter((limit) => limit !== primary)
        .map((limit) => ({limit_field: limit.limitField, requested_value: limit.requested})),
    });
    return !!isLimitHit;
  };

  const isOverUsageLimit = async (): Promise<boolean> => {
    const freshUsage = await refetchUsage();
    const getTier = buildCheckLimitTierLookup(false, freshUsage, undefined);
    const over = getCheckLimitStatuses(newProposals, newSelection, getTier).filter(
      (status) => status.selected > status.remaining
    );
    if (over.length) {
      setOverLimitStatuses(over);
      return true;
    }
    return false;
  };

  const [confirmValues, setConfirmValues] = useState<AnalyzerCreateFormValues | null>(null);

  const handleCreate = methods.handleSubmit(async (values) => {
    if (!selectedProposals.length || !formData) {
      return;
    }
    if (await (isBilling2 ? isOverBilling2Limit() : isOverUsageLimit())) {
      return;
    }
    setConfirmValues(values);
  });

  const confirmCreate = useCallback(async () => {
    if (!confirmValues) {
      return;
    }
    try {
      await runCreate(confirmValues);
    } finally {
      setConfirmValues(null);
    }
  }, [confirmValues, runCreate]);

  const openUpgradeModal = () => {
    if (isBilling2) {
      isOverBilling2Limit();
    } else {
      isOverUsageLimit();
    }
  };

  const cancelConfirm = useCallback(() => {
    setConfirmValues(null);
    setSnippets([]);
  }, []);

  const goToChecks = useCallback(() => {
    setSnippets([]);
    navigate(checksUrlForTags(creationOutcome?.tagIds ?? []));
  }, [creationOutcome, navigate]);

  const dismissOverLimit = useCallback(() => setOverLimitStatuses([]), []);

  const canCreate = phase === AnalyzerPhase.CHECK_SELECTION && selectedProposals.length > 0 && !!formData;
  const isCreating = isMutating || methods.formState.isSubmitting;

  return {
    methods,
    isCreating,
    canCreate,
    canPurchase,
    createLabel,
    isGrowOnly,
    newSelection,
    selectedRumCount,
    createdRumCount,
    snippets,
    creationOutcome,
    handleCreate,
    openUpgradeModal,
    showConfirm: !!confirmValues || snippets.length > 0,
    confirmCreate,
    cancelConfirm,
    goToChecks,
    limitCheckRef,
    overLimitStatuses,
    dismissOverLimit,
  };
};
